/* Bottom prompt bar — auto-grow textarea, model select, Send,
   busy status ("{Model} is thinking… M:SS") and the
   telemetry chip. Cmd/Ctrl+Enter sends. */

import React, { useRef, useState } from 'react';
import { ArrowUp, MessageCircle, Pencil, X } from 'lucide-react';
import { LatoOrb, useLatoAiPanelDrag } from './LatoAi';
import { StageAiButton } from './StageChrome';
import { fmtElapsed, fmtK, autoGrow } from './utils';
import { Telemetry } from './types';

interface Props {
  active?: boolean;
  model: string;
  setModel: (v: string) => void;
  running: boolean;
  thinking: boolean; // running && this is a prompt run
  elapsedMs: number;
  telemetry: Telemetry | null;
  onSend: (instruction: string) => Promise<boolean>;
}

function telemetryText(t: Telemetry): string {
  const inT = t.inputTokens ?? t.input_tokens ?? 0;
  const outT = t.outputTokens ?? t.output_tokens ?? 0;
  const cr = t.cacheReadTokens ?? t.cache_read_input_tokens ?? 0;
  const cw = t.cacheCreationTokens ?? t.cache_creation_input_tokens ?? 0;
  const cost = t.costUsd ?? t.cost_usd ?? t.total_cost_usd;
  const parts = ['tokens ↑' + fmtK(inT) + ' ↓' + fmtK(outT)];
  if (cr + cw > 0) parts.push('cache ' + fmtK(cr + cw));
  if (typeof cost === 'number') parts.push('$' + cost.toFixed(cost < 0.1 ? 3 : 2));
  return parts.join(' · ');
}

export default function PromptBar({
  active = true,
  model,
  running,
  thinking,
  elapsedMs,
  telemetry,
  onSend,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [lastSent, setLastSent] = useState('');
  const send = async () => {
    if (running || !input.trim()) return;
    const instruction = input;
    const ok = await onSend(instruction);
    if (ok) {
      setLastSent(instruction);
      setInput('');
      if (taRef.current) { taRef.current.value = ''; autoGrow(taRef.current); }
    }
  };
  const isOpen = open && active;
  const panelDrag = useLatoAiPanelDrag(isOpen);
  return (
    <div className="arch-architecture-ai-dock">
      <StageAiButton open={isOpen} busy={thinking} onClick={() => setOpen(!open)} />
      <aside ref={panelDrag.panelRef} style={panelDrag.panelStyle} className={'lato-architecture-ai lato-ai-panel' + (isOpen ? ' open' : '') + panelDrag.panelClass}
        aria-label="Lato AI architecture editor" aria-hidden={!isOpen} inert={!isOpen}
        onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }}>
        <div className="arch-chat-panel">
          <header className="arch-chat-head" {...panelDrag.handleProps} title="Drag to move Lato AI">
            <LatoOrb animated={isOpen} busy={thinking} />
            <div className="arch-chat-head-text"><div className="arch-chat-title">Lato AI</div><div className="arch-chat-hint">Edit your architecture.</div></div>
            <button className="arch-chat-headbtn" aria-label="Close Lato AI" onClick={() => setOpen(false)}><X size={16} /></button>
          </header>
          <div className="arch-chat-list">
            {lastSent ? <div className="arch-chat-msg user"><div className="arch-chat-bubble user"><span className="arch-chat-tag">Edit</span>{lastSent}</div></div> :
              <div className="arch-chat-empty">
                <div className="arch-chat-empty-mark"><LatoOrb /></div>
                <div className="arch-chat-empty-title">What would you like to change?</div>
                <div className="arch-chat-empty-chips">{['Rename the first case', 'Add a follow-up step'].map(prompt => <button className="arch-chat-quick" key={prompt} onClick={() => { setInput(prompt); taRef.current?.focus({ preventScroll: true }); }}>{prompt}</button>)}</div>
              </div>}
            {thinking && <div className="arch-chat-msg assistant"><div className="arch-chat-bubble assistant pending" role="status">Lato AI is updating the architecture… {fmtElapsed(elapsedMs)}</div></div>}
          </div>
          <form className="arch-chat-composer" onSubmit={e => { e.preventDefault(); void send(); }}>
            <div className="arch-chat-modes" role="group" aria-label="Assistant mode">
              <button type="button" className="arch-chat-mode" disabled title="Questions are not available for architecture yet"><MessageCircle size={14} /> Ask</button>
              <button type="button" className="arch-chat-mode" aria-pressed="true" onClick={() => taRef.current?.focus({ preventScroll: true })}><Pencil size={14} /> Edit</button>
            </div>
            <textarea id="arch-prompt-input" className="arch-chat-input" ref={taRef} rows={2} value={input}
              aria-label="Describe a change to your architecture" placeholder="Describe a change to your architecture…"
              onChange={e => { setInput(e.target.value); autoGrow(e.target); }}
              onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void send(); } }} />
            <div className="arch-chat-composer-row">

              <button type="submit" className="arch-btn arch-btn-primary arch-chat-send" disabled={running || !input.trim()} title="Send edit (⌘ / Ctrl + Enter)"><ArrowUp size={19} /><span className="lato-ai-sr">Send</span></button>
            </div>
            {telemetry && <div className="arch-chat-tele">{telemetryText(telemetry)}</div>}
          </form>
        </div>
      </aside>
    </div>
  );
}
